import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from 'classnames';
import { useSSRSafeId as $b5e257d569688ac6$export$619500959fc48b26 } from '../../node_modules/@react-aria/ssr/dist/import.js';
import styles from './TextArea.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { FormHelperText } from '../Form/FormHelperText.js';
import { FormLabel } from '../Form/FormLabel.js';
import { debounce } from '../utility/debounce.js';
import { VisuallyHidden } from '@walmart-web/livingdesign-components';

var _excluded = ["className", "disabled", "error", "helperText", "id", "label", "maxLength", "maxLengthA11yAnnouncement", "onChange", "placeholder", "readOnly", "size", "textAreaProps", "value"];
/**
 * Text Areas allow for the input of freeform, often multi-line, text.
 *
 * {@link https://digitaltoolkit.livingdesign.walmart.com/components/text-area/ Guidelines}
 * {@link https://digitaltoolkit.livingdesign.walmart.com/develop/react/components/text-area/ React documentation}
 *
 * %DOCUMENTATION%
 */
var TextArea = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, TextArea),
    className = _applyCommonProps.className,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    error = _applyCommonProps.error,
    helperText = _applyCommonProps.helperText,
    initialId = _applyCommonProps.id,
    label = _applyCommonProps.label,
    maxLength = _applyCommonProps.maxLength,
    maxLengthA11yAnnouncement = _applyCommonProps.maxLengthA11yAnnouncement,
    onChange = _applyCommonProps.onChange,
    placeholder = _applyCommonProps.placeholder,
    _applyCommonProps$rea = _applyCommonProps.readOnly,
    readOnly = _applyCommonProps$rea === void 0 ? false : _applyCommonProps$rea,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'large' : _applyCommonProps$siz,
    textAreaProps = _applyCommonProps.textAreaProps,
    _applyCommonProps$val = _applyCommonProps.value,
    value = _applyCommonProps$val === void 0 ? '' : _applyCommonProps$val,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var id = $b5e257d569688ac6$export$619500959fc48b26(initialId);
  var helperId = $b5e257d569688ac6$export$619500959fc48b26();
  var _React$useState = React.useState(''),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    screenReaderMessage = _React$useState2[0],
    setScreenReaderMessage = _React$useState2[1];

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  var setDelayedScreenReaderMessage = React.useMemo(function () {
    return debounce(setScreenReaderMessage, 1500);
  }, []);
  React.useEffect(function () {
    if (!maxLength) {
      return function () {
        return undefined;
      };
    }
    setDelayedScreenReaderMessage(maxLengthA11yAnnouncement !== null && maxLengthA11yAnnouncement !== void 0 ? maxLengthA11yAnnouncement : "".concat(maxLength - value.length, " characters left."));
    return function () {
      setDelayedScreenReaderMessage.cancel();
    };
  }, [maxLength, setDelayedScreenReaderMessage, maxLengthA11yAnnouncement, value]);
  var hasHelperContent = !!(error || helperText || maxLength);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classNames(styles.root, disabled && styles.disabled, error && styles.error, readOnly && styles.readOnly, size === 'large' && styles.large, size === 'small' && styles.small, className)
  }, rest), /*#__PURE__*/React.createElement(FormLabel, {
    className: styles.label,
    disabled: disabled,
    htmlFor: id,
    size: size
  }, label), /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement("textarea", _extends({
    "aria-describedby": hasHelperContent ? helperId : undefined,
    disabled: disabled,
    id: id,
    maxLength: maxLength,
    onChange: onChange,
    placeholder: placeholder,
    readOnly: readOnly,
    ref: ref,
    value: value
  }, textAreaProps, {
    className: classNames(styles.value, textAreaProps === null || textAreaProps === void 0 ? void 0 : textAreaProps.className)
  }))), /*#__PURE__*/React.createElement("div", {
    id: helperId
  }, hasHelperContent && /*#__PURE__*/React.createElement("div", {
    className: styles.helperTextContainer
  }, /*#__PURE__*/React.createElement(FormHelperText, {
    disabled: disabled,
    hasError: !!error
  }, error || helperText), maxLength && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": true,
    className: styles.maxLength
  }, value.length, " / ", maxLength)), /*#__PURE__*/React.createElement(VisuallyHidden, {
    "aria-atomic": true,
    "aria-live": "polite"
  }, screenReaderMessage)));
});
TextArea.displayName = 'TextArea';

export { TextArea };
